import { MaybeRefOrGetter, onScopeDispose, readonly, Ref, ref, toValue, watch } from "vue";

type ElementRef<T extends HTMLElement> = Ref<T | null | undefined>;

interface UseTokenInputDraftSizeOptions {
  textarea: ElementRef<HTMLTextAreaElement>;
  mirror: ElementRef<HTMLElement>;
  wrapper: ElementRef<HTMLElement>;
  text: MaybeRefOrGetter<string>;
}

interface UseTokenInputDraftSize {
  isFullWidth: Readonly<Ref<boolean>>;
  update: () => void;
}

export const DS_TOKEN_INPUT_DRAFT_WIDTH_PROPERTY = "--DSTokenInput-DraftWidth";

export const useTokenInputDraftSize = ({
  textarea,
  mirror,
  wrapper,
  text,
}: UseTokenInputDraftSizeOptions): UseTokenInputDraftSize => {
  const isFullWidth = ref(false);

  const update = (): void => {
    const draft = textarea.value;
    const measure = mirror.value;
    const field = wrapper.value;

    if (!draft || !measure || !field) {
      return;
    }

    measure.textContent = toValue(text);

    const fieldWidth = field.clientWidth;
    const textWidth = measure.getBoundingClientRect().width;

    isFullWidth.value = fieldWidth > 0 && textWidth >= fieldWidth;
    draft.style.setProperty(DS_TOKEN_INPUT_DRAFT_WIDTH_PROPERTY, `max(1ch, ${Math.min(textWidth, fieldWidth)}px)`);

    // Legacy form styles force textarea heights with !important on small screens.
    draft.style.setProperty("height", "auto", "important");
    draft.style.setProperty("height", `${draft.scrollHeight}px`, "important");
  };

  watch([() => toValue(text), textarea, mirror, wrapper], update, { flush: "post", immediate: true });

  if (typeof ResizeObserver !== "undefined") {
    const observer = new ResizeObserver(update);

    watch(
      wrapper,
      element => {
        observer.disconnect();

        if (element) {
          observer.observe(element);
        }
      },
      { immediate: true }
    );

    onScopeDispose(() => observer.disconnect());
  }

  return { isFullWidth: readonly(isFullWidth), update };
};
